import {
  Box,
  Button,
  Typography,
  Hidden,
  IconButton,
  Tooltip,
  Stack,
  Switch,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  enableDisableUser,
  getAMCAuthorisedSignatoryList,
  getAMCAuthorisedSignatoryTableListing,
  getRequiredForEsign,
  getRequiredForEsignType,
  setRequiredForEsign,
  UpdateAMCAuthorisedSignatory,
} from '../../redux-store/actions/userManagement';
import {
  AddAmcAuthorisedSignatoryType,
  AMCAuthorisedSignatoryListingType,
  AMCAuthorisedSignatoryListType,
} from '../../redux-store/types/api-types';
import { ColumnType, DataTable } from '../DataTable';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmationDialog } from '../commonComponents';
import { AccessHeader } from './relationShipManagers';
import { getAmcAuthorisedMaster } from '../../utils/utilityFunctions';
import { RootStateType } from '../../redux-store/reducers';
import { CursorDirection, USER_ROLES } from '../../utils/constant';

const columns: ColumnType[] = [
  {
    header: 'Name',
    renderCell: (row: AMCAuthorisedSignatoryListType) => {
      return (
        <Typography
          variant="h5"
          sx={{
            color: '#61D1D6',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '14px',
            ':hover': {
              color: 'primary.main',
            },
          }}
          component={RouterLink}
          to={{
            pathname: 'amc-authorised-signatory-details',
            state: { amcAuthorisedSignatoryId: row.id },
          }}>
          {row?.firstName || 'N/A'}
        </Typography>
      );
    },
  },
  {
    header: 'Mobile Number',
    valueGetter: (row: AMCAuthorisedSignatoryListType) => row?.phone || 'N/A',
  },
  {
    header: 'Email ID',
    valueGetter: (row: AMCAuthorisedSignatoryListType) => row?.email || 'N/A',
  },
];

function AMCAuthorisedSignatory(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [amcAuthorisedSignatoryList, setAmcAuthorisedSignatoryList] = useState<
    AMCAuthorisedSignatoryListType[]
  >([]);
  const [requiredEsignVal, setRequiredEsignVal] = useState<number[]>([]);
  const [RequireEsignValueRes, SetRequireEsignValueRes] = useState(0);

  const {
    page: storePage,
    sortDirection: storeSortDirection,
    rowsPerPage: storeRowsPerPage,
    nextCursor: storeNextCursor,
    prevCursor: storePrevCursor,
    cursorDirection: storeCursorDirection,
    hasNext: storeHasNext,
    hasPrev: storeHasPrev,
    currentCursor: storeCurrentCursor,
    paginationRole,
  } = useSelector((store: RootStateType) => store.paramsObj);

  const isPaginationRoleIsRM = paginationRole === USER_ROLES?.AMCAUTHORISEDSIGNATORY;

  const [currentPage, setCurrentPage] = useState(isPaginationRoleIsRM ? storePage || 1 : 1);
  const [openDialog, setDialog] = useState<{
    amcAuthorisedSignatoryRowData: AddAmcAuthorisedSignatoryType;
  } | null>(null);
  const [hasNext, setHasNext] = useState(isPaginationRoleIsRM ? storeHasNext || false : false);
  const [hasPrev, setHasPrev] = useState(isPaginationRoleIsRM ? storeHasPrev || false : false);
  const [rowsPerPage, setRowsPerPage] = useState(
    isPaginationRoleIsRM ? storeRowsPerPage || 10 : 10
  );
  const [currentCursor, setCurrentCursor] = useState<string | null>(
    isPaginationRoleIsRM ? storeCurrentCursor || null : null
  );
  const [nextCursor, setNextCursor] = useState<string | null>(
    isPaginationRoleIsRM ? storeNextCursor || null : null
  );
  const [prevCursor, setPrevCursor] = useState<string | null>(
    isPaginationRoleIsRM ? storePrevCursor || null : null
  );
  const [cursorDirection, setCursorDirection] = useState<CursorDirection | undefined>(undefined);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);

        const params = {
          limit: rowsPerPage,
          order: 'DESC',

          ...(currentCursor
            ? {
                cursor: currentCursor,
                ...(cursorDirection && { direction: cursorDirection }),
              }
            : {
                cursor: null,
              }),
        };

        const RequiredForEsign = (await dispatch(
          getRequiredForEsign()
        )) as unknown as getRequiredForEsignType;
        SetRequireEsignValueRes(RequiredForEsign.requiredAmcAuthorisedSignatoriesForEsign);
        const res = (await dispatch(
          getAMCAuthorisedSignatoryTableListing(params)
        )) as unknown as AMCAuthorisedSignatoryListingType;
        setRequiredEsignVal(
          getAmcAuthorisedMaster(res?.data?.filter((isActiveData) => isActiveData.isActive).length)
        );
        const { data, nextCursor: nextC, prevCursor: prevC, hasNext, hasPrev } = res || {};

        if (!isComponentAlive) return;

        setNextCursor(nextC || '');
        setPrevCursor(prevC || '');
        setHasNext(hasNext || false);
        setHasPrev(hasPrev || false);
        setLoading(false);

        setAmcAuthorisedSignatoryList(data);
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isComponentAlive = false;
    };
  }, [currentPage, rowsPerPage, currentCursor, cursorDirection]);

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page
  };

  const handleCursorPageChange = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    // If user clicks next
    if (newPage > currentPage - 1 && nextCursor) {
      setCurrentCursor(nextCursor);
      setCursorDirection(CursorDirection.NEXT);
    }
    // If user clicks prev
    else if (newPage < currentPage - 1 && prevCursor) {
      setCurrentCursor(prevCursor);
      setCursorDirection(CursorDirection.PREV);
    }
    // Optionally update currentPage if you want to show the page number
  };

  const AccessCell = [
    {
      header: AccessHeader,
      renderCell: (row: AddAmcAuthorisedSignatoryType): JSX.Element => {
        return (
          <>
            <Stack
              sx={{
                alignItems: { xs: 'baseline', sm: 'center' },
                '.MuiSwitch-root': {
                  paddingLeft: { xs: '0px', sm: '12px' },
                  width: { xs: '52px', sm: '58px' },
                },
                '.MuiSwitch-switchBase': { paddingLeft: { xs: '0px', sm: '9px' } },
              }}>
              <Switch
                sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                checked={row.isActive}
                onChange={() => setDialog({ amcAuthorisedSignatoryRowData: row })}
              />
            </Stack>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Box sx={{ bgcolor: 'common.white', px: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            pt: { xs: 2, sm: 7 },
            pb: { xs: 2, sm: 4 },
          }}>
          <Typography component="h2" sx={{ fontSize: { xs: 15, sm: 25 }, fontWeight: 500 }}>
            List of AMC Authorized Signatory
          </Typography>
          <Grid
            sx={{
              display: 'flex',
            }}
            alignItems="center">
            <Typography component="h2" sx={{ color: 'primary.main', fontSize: '16px', ml: 3 }}>
              No. of Authorized signatories to esign:
            </Typography>
            <Box
              sx={{
                width: '100px',
                ml: { md: 1 },
                '.MuiOutlinedInput-root': {
                  marginTop: '0',
                  backgroundColor: 'rgba(97, 209, 214, 0.07)',
                  '&.MuiInputBase-root': {
                    '& .MuiSelect-select': {
                      padding: '7px 38px',
                      border: 'none',
                    },
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
                '&.Mui-focused,&:hover': {
                  '.MuiOutlinedInput-notchedOutline': {
                    borderWidth: 'none',
                  },
                },
                svg: {
                  color: '#337FC9',
                },
              }}>
              <Select
                labelId="user-items"
                onChange={async (e) => {
                  try {
                    const resSetForEsign = await dispatch(
                      setRequiredForEsign({
                        requiredAmcAuthorisedSignatoriesForEsign: Number(e.target.value),
                      }) as unknown as getRequiredForEsignType
                    );
                    SetRequireEsignValueRes(
                      resSetForEsign.requiredAmcAuthorisedSignatoriesForEsign
                    );
                  } catch (e) {
                    console.error((e as Error).message);
                  }
                }}
                value={RequireEsignValueRes}
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                }}>
                {requiredEsignVal.map((thresholdValue, ind) => (
                  <MenuItem
                    sx={{
                      borderBottom: '1px solid #ccc',
                      textAlign: 'center',
                      '&:last-child': { border: 'none' },
                      justifyContent: 'center',
                    }}
                    value={thresholdValue}
                    key={ind}>
                    {thresholdValue}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Grid>
          <Hidden only="xs">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: 14,
                mr: 2.5,
                py: 0.5,
                px: 3,
              }}
              onClick={() => history.push('add-amc-authorised-signatory')}>
              Add AMC Authorized Signatory
            </Button>
          </Hidden>
          <Hidden smUp={true}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontSize: 13,
                // mr: 2.5,
                py: 0.5,
                // px: 3,
                // whiteSpace: 'nowrap',
              }}
              onClick={() => history.push('add-amc-authorised-signatory')}>
              Add AMC Authorise Signatory
            </Button>
          </Hidden>
        </Box>
        {useMemo(
          () => (
            <DataTable
              tableData={amcAuthorisedSignatoryList}
              tableHeader={[...columns, ...AccessCell]}
              currentPage={currentPage}
              isLoading={isLoading}
              noOfPages={1}
              paginationAction={handlePageChange}
              renderAdditionalRow={true}
              tableBodyCustomStyles={{
                '.MuiTableCell-root': {
                  borderBottom: 'none',
                },
              }}
              tableHeaderCustomStyles={{
                '&:last-child th': { border: 0 },
                '.MuiTableCell-head': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'primary.main',
                },
              }}
              rowCustomStyles={{
                '&:last-child td, &:last-child th': { border: 0 },
                '.MuiTableCell-body ': { color: '#686e72' },
                background: 'transparent',
              }}
              handleCursorPageChange={handleCursorPageChange}
              hasNext={hasNext}
              hasPrev={hasPrev}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              rowsPerPage={rowsPerPage}
            />
          ),
          [amcAuthorisedSignatoryList, currentPage, isLoading]
        )}
      </Box>
      <ConfirmationDialog
        message={`Are you sure you want to ${
          openDialog?.amcAuthorisedSignatoryRowData.isActive ? 'disable' : 'enable'
        } the access to this user ?`}
        open={openDialog !== null}
        setOpen={() => setDialog(null)}
        onSave={async () => {
          setDialog(null);
          try {
            if (openDialog) {
              await dispatch(
                enableDisableUser({
                  role: USER_ROLES.AMCAUTHORISEDSIGNATORY,
                  roleId: openDialog?.amcAuthorisedSignatoryRowData?.id as number,
                  status: openDialog?.amcAuthorisedSignatoryRowData?.isActive
                    ? 'disable'
                    : 'enable',
                })
              );
              const updatedRowList = amcAuthorisedSignatoryList?.map((row) => {
                if (row.id === openDialog?.amcAuthorisedSignatoryRowData.id) {
                  return { ...row, isActive: !openDialog?.amcAuthorisedSignatoryRowData.isActive };
                }
                return row;
              });
              setAmcAuthorisedSignatoryList(updatedRowList);
            }
          } catch (e) {
            console.error((e as Error).message);
          }
        }}
        onCancel={() => setDialog(null)}
      />
    </>
  );
}

export default AMCAuthorisedSignatory;
